import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'

import { useQuery } from 'jsonapi-react'
import { Twitter, Instagram, Facebook } from 'react-feather'
import { format } from 'date-fns'
import BasicInfoSection from './BasicInfoSection'
import BookACallSection from './BookACallSection'
import TopMatches from './TopMatches'
import RequestedTrucks from './RequestedTrucks'
import LazyImage from 'app/theme/LazyImage'
import LeadSuccess from './LeadSuccess'
import { getDaysUntil } from './dashboard-utils'
import logo from 'assets/svg/logo-bg.svg'
import guarantee from 'assets/svg/guarantee.svg'
import env from 'env'
import styles from './LeadDashboardPage.module.css'
import { usePostHog } from 'lib/posthog'

export default function LeadDashboardPage() {
  const params = useParams()
  const history = useHistory()
  const { posthog } = usePostHog()

  const query = useQuery(['leads', {
    token: params.leadToken,
    include: [
      'occasion',
      'contact',
      'proposal',
      'experience_manager',
      'experience_manager.photo'
    ],
  }])

  const lead = query.data && query.data[0]

  const caterersQuery = useQuery(lead && ['caterers', {
    filter: {
      active: true,
      closed: false,
      standing: ['good', 'risky'],
      tag_id: lead.cuisines,
      favorite_cuisine: lead.favorite_cuisine_id || undefined,
      packages: {
        instant_book: 'true'
      },
      service_area: {
        contains: JSON.stringify({ ...lead.address.location, radius: 10 }),
      }
    },
    sort: [
      lead.favorite_cuisine_id && 'favorite_cuisine',
      '-average_rating',
    ].filter(Boolean),
    include: [
      'photos',
      'photo',
      'logo',
      'default_package_photo',
      'tags',
      'good_fors',
      'services',
      'cities',
      'caterer_stat'
    ],
    fields: {
      caterers: ['id', 'name', 'slug', 'description', 'short_description', 'ranking_tier'],
      photos: ['uuid', 'extension', 'path'],
      tags: ['name'],
      good_fors: ['name'],
      caterer_services: ['type', 'area_address']
    },
    page: {
      size: 6,
      limit: 6,
    },
    stats: {
      total: 'count',
    }
  }])

  const topMatchingCaterers = caterersQuery.data

  useEffect(() => {
    if (!query.isLoading && query.data[0] && !caterersQuery.isLoading ) {
      posthog.capture('$pageview', {
        lead_id: query.data[0].id,
        contact_id: query.data[0].contact?.id,
        top_vendors_count: topMatchingCaterers.length || 0,
        top_vendors_list: topMatchingCaterers[0] ? topMatchingCaterers.map(c => ({id: c.id, name: c.name })) : []
      })
    }
  }, [query.isLoading, caterersQuery.isLoading])

  if (query.isLoading) {
    return <div className='w-full h-full min-h-screen flex flex-col items-center justify-center'>
      <LazyImage
        src={logo}
        alt="Roaming Hunger"
        className='h-12 animate-ping-slow duration-1000'
      />
      <h1 className='font-platform text-black text-3xl md:text-5xl pt-4'>
        Preparing for your event<span className='animate-pulse'>...</span>
      </h1>
    </div>
  }

  if (!lead) {
    return (
      <div className="w-full h-full min-h-screen flex items-center" style={{ backgroundColor: '#00aeef' }}>
        <div className="max-w-xl m-auto">
          <LeadSuccess
            data={{}}
            catererName={null}
            closeModal={() => {
              // go back to the previous URL where the lead was submitted
              history.goBack()
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">

      <div className='bg-white flex justify-center p-4 border-b border-blue-300'>
        <LazyImage className='h-12' src={logo} title='Roaming Hunger' alt='Roaming Hunger'/>
      </div>

      <div className="flex-1 mb-4">
        <div className='bg-gray-100'>
          <div className='container py-6'>
              <h1 className='font-platform text-black text-3xl md:text-5xl pt-4'>
                {lead.contact.first_name}'s {lead.occasion?.display_name || lead.occasion?.name || 'Event'}
              </h1>
            <div className='grid md:grid-cols-2 gap-4 py-4'>
              {/* Lead Information section */}
              <BasicInfoSection lead={lead} />

              {/* Account/Event Manager section */}
              <div className=''>
                <BookACallSection lead={lead}/>
              </div>
            </div>
          </div>
        </div>

        <div className='bg-white'>
          {/* Top Vendor Matches section */}
          <TopMatches lead={lead} loading={caterersQuery.isLoading} caterers={topMatchingCaterers} />
        </div>

        <div className='container'>
          <div className='grid md:grid-cols-2 gap-4 py-4'>
            <div className='bg-white'>
              {/* Requested Trucks section */}
              <h2 className='text-3xl md:text-5xl font-platform text-black'>Requested Quotes</h2>
              <RequestedTrucks lead={lead} />
            </div>
            {/* Contact section */}
            <div className='text-black'>
              <h2 className='text-3xl md:text-5xl font-platform'>Need help?</h2>
              <div className={[styles.card, 'leading-tight text-lg font-light mt-4']}>
                <div className='pb-2'>
                  <h4 className='font-bold'>Email us</h4>
                  <p className="text-blue font-bold">
                    <a href="mailto:catering@roaminghunger.com">catering@roaminghunger.com</a>
                  </p>
                </div>
                <div>
                  <h4 className='font-bold'>Call us</h4>
                  <p className='text-lg'>
                    Monday - Friday
                  </p>
                  <p className='text-lg'>
                    9-5pm PST
                  </p>
                  <p className='text-lg text-blue font-bold'>
                    <a href="tel:18005689370">1-800-568-9370</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='bg-black py-4'>
        <div className='container'>
          <div className='flex flex-col items-center justify-center text-white mb-3'>
            <LazyImage src={logo} className='h-12' alt="Roaming Hunger" />
            <div className="grid grid-cols-3 gap-4 mt-4">
              <a href="https://twitter.com/roaminghunger" target="_blank" rel="noreferrer">
                <Twitter />
              </a>
              <a href="https://www.instagram.com/roaminghunger/" target="_blank" rel="noreferrer">
                <Instagram />
              </a>
              <a href="https://www.facebook.com/roaminghunger" target="_blank" rel="noreferrer">
                <Facebook />
              </a>
            </div>
          </div>
          <div className='text-white text-sm font-light text-center'>
            &copy; {format(new Date(), 'yyyy')} Roaming Hunger
          </div>
          <div className='text-white text-sm font-light text-center'>
            Version: {env.APP_VERSION || 'n/a'}
          </div>
        </div>
      </div>
    </div>
  )
}
